import React from "react";
import GuidePanel from "../GuidePanel.js";
import {
  GuideLead,
  GuideCallout,
  GuideSectionLabel,
  GuideCard,
  GuideCardGrid,
  GuideBlockList,
} from "../GuideBlocks.js";

const DecisionConfidenceSection = ({ heading, accentIndex = 1, getVisual, getAccent }) => (
  <GuidePanel
    sectionId="decision-confidence"
    heading={heading}
    accentIndex={accentIndex}
    getVisual={getVisual}
    getAccent={getAccent}
  >
    <GuideLead>
      Most people think confident decision-makers feel certain. They do not. They have learned to
      decide well without certainty.
    </GuideLead>
    <p className="guide-panel__p">
      <strong>Decision confidence</strong> is the ability to commit to a choice and stand behind it,
      even when you cannot guarantee the outcome. It is a skill built through practice — not a
      personality trait you either have or lack.
    </p>
    <GuideSectionLabel>Why confidence feels hard to find</GuideSectionLabel>
    <p className="guide-panel__p">
      Waiting to &quot;feel sure&quot; before deciding is a common trap. Certainty rarely arrives
      before a decision — it usually arrives after you have seen how things unfold. If you wait for
      it, you wait forever, or you decide too late to matter.
    </p>
    <GuideCallout>
      What produces confidence is not more information. It is a process you trust, even when the
      outcome is unknown.
    </GuideCallout>
    <GuideSectionLabel>Where EIER builds confidence</GuideSectionLabel>
    <p className="guide-panel__p">
      In the EIER framework — <strong>Event → Interpretation → Evidence → Response</strong> — most
      decision pressure lives in the gap between Interpretation and Response. Urgency arrives, and
      it feels like the decision must happen now, before you have examined the interpretation
      driving it or checked it against evidence.
    </p>
    <p className="guide-panel__p">Confidence grows when you slow that gap down:</p>
    <GuideBlockList
      items={[
        "1. Name the Event — what actually happened, stripped of story.",
        "2. Check the Interpretation — is this the only reasonable read, or just the fastest one?",
        "3. Gather the Evidence — what supports this read, and what contradicts it?",
        "4. Choose the Response — deliberately, not reflexively.",
      ]}
    />
    <GuideCallout variant="amber">
      Confidence is not the absence of doubt at step 4. It is trusting that you did not skip steps
      1–3.
    </GuideCallout>
    <GuideSectionLabel>Building decision confidence in practice</GuideSectionLabel>
    <GuideCardGrid columns={2}>
      <GuideCard
        iconKey="clarity"
        title="Separate decision from outcome"
        description="A sound process can still lead to a hard outcome — that does not make the call wrong in hindsight."
      />
      <GuideCard
        iconKey="thinking"
        title="Track your process"
        description="Keep a short log of major decisions and why you made them. Confidence compounds when you can see your reasoning held up."
      />
      <GuideCard
        iconKey="energy"
        title="Set a decision deadline"
        description="Open-ended deliberation erodes confidence more than it protects you."
      />
      <GuideCard
        iconKey="growth"
        title="Match effort to reversibility"
        description="Reversible decisions deserve less deliberation. Save energy for the ones that cannot be undone."
      />
    </GuideCardGrid>
    <GuideCallout>
      Confidence is not a feeling you wait for. It is a habit you build one clear decision at a
      time.
    </GuideCallout>
  </GuidePanel>
);

export default DecisionConfidenceSection;
